import type { Language } from "../config/site";
import type { LegalDocument } from "./shared";

export const termsDocuments: Record<Language, LegalDocument> = {
  en: {
    eyebrow: "LEGAL",
    title: "Terms of Use",
    updatedLabel: "Last updated",
    updatedDate: "August 24, 2026",
    introduction: "These terms govern your use of the VaultPin native iOS game, the Daily Vault online service and this website. VaultPin is provided by Miraç Doğan in Türkiye.",
    sections: [
      {
        heading: "1. Acceptance of these terms",
        paragraphs: [
          "By downloading, accessing or using VaultPin, you agree to these terms and the applicable Apple terms. If you do not agree, do not use the service. Mandatory consumer rights under applicable law are not excluded by these terms.",
        ],
      },
      {
        heading: "2. Age and legal capacity",
        paragraphs: [
          "You must have the legal capacity required in your location to accept these terms. If local law requires permission from a parent or legal guardian, you must obtain that permission before using VaultPin or making a purchase.",
        ],
      },
      {
        heading: "3. The VaultPin service",
        paragraphs: [
          "VaultPin is a code-breaking logic game. It includes a shared online Daily Vault and local practice modes. Features may depend on a compatible iPhone, iOS version, internet connection and Apple services.",
          "VaultPin is not gambling, a real-money game or a prize competition. It offers no cash prize, transferable value or chance-based wagering.",
        ],
      },
      {
        heading: "4. Daily Vault rules",
        paragraphs: [
          "The Daily Vault uses one shared four-digit code during a backend-defined cycle. Digits do not repeat, leading zero is valid, and the attempt and hint limits shown in the app apply. The backend is authoritative for the active cycle, timing, guesses, hint state, result and eligibility.",
          "A session begins timing on the first meaningful action defined by the game. Leaving or closing the app does not pause an active Daily Vault timer. Network availability may be required to start, continue or recover the session.",
        ],
      },
      {
        heading: "5. Game Center and leaderboard",
        paragraphs: [
          "Game Center is optional for playing as a guest but is required for eligible leaderboard participation. Apple authenticates the Game Center account. A verified Game Center alias may be visible to other players with rank, attempt count, time and hint-use information.",
          "Guest sessions may view a leaderboard but are not eligible for ranking. VaultPin does not promise that an alias will be unique, available or appropriate, and Apple controls the Game Center account and profile.",
        ],
      },
      {
        heading: "6. Fair use",
        paragraphs: [
          "Do not use automation, scripts, reverse engineering, tampered clients, forged identities, API abuse, denial-of-service techniques or other methods to manipulate gameplay, timing or leaderboards. Do not attempt to access another player's session or data.",
          "VaultPin may reject invalid requests, remove ineligible results or restrict abusive access when reasonably necessary to protect the service and other players.",
        ],
      },
      {
        heading: "7. One-time purchase",
        paragraphs: [
          "Medium, Hard and Expert practice modes are offered together as one non-consumable StoreKit purchase. There is no subscription. The price shown by Apple may vary by store, currency and tax rules.",
          "Apple processes payment, restoration and refund requests under its terms. Purchase availability and restoration can depend on the Apple account and StoreKit. Deleting VaultPin gameplay data does not remove the Apple-managed purchase entitlement.",
        ],
      },
      {
        heading: "8. Apple terms",
        paragraphs: [
          "Use of the iOS app is also subject to Apple's applicable rules, including the Apple Standard Licensed Application End User License Agreement unless a different valid agreement applies. The official standard EULA is available at https://www.apple.com/legal/internet-services/itunes/dev/stdeula/.",
        ],
      },
      {
        heading: "9. Intellectual property",
        paragraphs: [
          "VaultPin, its original interface, artwork, sounds, text, game presentation, software and branding are owned by Miraç Doğan or used under applicable rights. These terms give you a personal, limited, non-exclusive and non-transferable right to use the app; they do not transfer ownership.",
        ],
      },
      {
        heading: "10. Availability and changes",
        paragraphs: [
          "Online services can be interrupted by maintenance, provider outages, network conditions, security events or changes to Apple services. VaultPin may modify, suspend or discontinue features where reasonably necessary. No particular uninterrupted availability, leaderboard position or permanent retention of a Daily Vault result is promised.",
        ],
      },
      {
        heading: "11. Disclaimer",
        paragraphs: [
          "To the extent permitted by applicable law, VaultPin is provided on an “as available” basis without additional warranties that cannot reasonably be given for an online game. This does not limit warranties or remedies that cannot lawfully be excluded for consumers.",
        ],
      },
      {
        heading: "12. Limitation of liability",
        paragraphs: [
          "To the extent permitted by applicable law, VaultPin is not responsible for indirect or consequential losses arising from service interruption, loss of a leaderboard position or incompatibility outside its reasonable control. Nothing in these terms excludes liability that cannot legally be excluded, including mandatory consumer protections.",
        ],
      },
      {
        heading: "13. Termination and data deletion",
        paragraphs: [
          "You may stop using VaultPin at any time and can use the in-app deletion control for VaultPin player and Daily Vault data. Deletion does not sign out of the system Game Center account or cancel Apple-managed purchases. If you use the Daily Vault later, a new player record may be created.",
        ],
      },
      {
        heading: "14. Governing law",
        paragraphs: [
          "These terms are governed by the laws of Türkiye, without taking away mandatory protections or dispute rights available to you under the law that applies in your place of residence. Competent courts and authorities remain determined by applicable law.",
        ],
      },
      {
        heading: "15. Contact and changes",
        paragraphs: [
          "Questions can be sent to support@vaultpin.app. These terms may be updated when the service or legal requirements change. The updated date identifies the current version; continued use after a change is subject to applicable notice and consent requirements.",
        ],
      },
    ],
  },
  tr: {
    eyebrow: "YASAL",
    title: "Kullanım Koşulları",
    updatedLabel: "Son güncelleme",
    updatedDate: "24 Ağustos 2026",
    introduction: "Bu koşullar VaultPin native iOS oyununu, Günlük Kasa çevrimiçi hizmetini ve bu web sitesini kullanımını düzenler. VaultPin, Türkiye'de Miraç Doğan tarafından sunulur.",
    sections: [
      {
        heading: "1. Koşulların kabulü",
        paragraphs: [
          "VaultPin'i indirerek, erişerek veya kullanarak bu koşulları ve geçerli Apple koşullarını kabul edersin. Kabul etmiyorsan hizmeti kullanma. Geçerli hukuktaki zorunlu tüketici hakları bu koşullarla ortadan kaldırılmaz.",
        ],
      },
      {
        heading: "2. Yaş ve yasal ehliyet",
        paragraphs: [
          "Bu koşulları kabul etmek için bulunduğun yerde gerekli yasal ehliyete sahip olmalısın. Yerel hukuk ebeveyn veya yasal vasi izni gerektiriyorsa VaultPin'i kullanmadan veya satın alma yapmadan önce bu izni almalısın.",
        ],
      },
      {
        heading: "3. VaultPin hizmeti",
        paragraphs: [
          "VaultPin bir kod çözme ve mantık oyunudur. Paylaşılan çevrimiçi Günlük Kasa ile yerel pratik modlarını içerir. Özellikler uyumlu iPhone, iOS sürümü, internet bağlantısı ve Apple hizmetlerine bağlı olabilir.",
          "VaultPin bahis, gerçek para oyunu veya ödüllü yarışma değildir. Nakit ödül, aktarılabilir değer veya şansa dayalı bahis sunmaz.",
        ],
      },
      {
        heading: "4. Günlük Kasa kuralları",
        paragraphs: [
          "Günlük Kasa, backend tarafından belirlenen cycle boyunca ortak bir dört haneli kod kullanır. Rakamlar tekrarlanmaz, leading zero geçerlidir ve uygulamada gösterilen tahmin ile ipucu sınırları uygulanır. Aktif cycle, süre, tahminler, ipucu durumu, sonuç ve uygunluk için backend belirleyicidir.",
          "Session süresi oyunun tanımladığı ilk anlamlı aksiyonda başlar. Uygulamadan ayrılmak veya uygulamayı kapatmak aktif Günlük Kasa süresini durdurmaz. Session'ı başlatmak, sürdürmek veya geri yüklemek için ağ bağlantısı gerekebilir.",
        ],
      },
      {
        heading: "5. Game Center ve sıralama",
        paragraphs: [
          "Misafir oynamak için Game Center zorunlu değildir ancak sıralamaya uygun katılım için gereklidir. Game Center hesabını Apple doğrular. Doğrulanmış Game Center alias'ı sıra, tahmin sayısı, süre ve ipucu kullanım bilgisiyle birlikte diğer oyunculara görünebilir.",
          "Misafir session'lar sıralamayı görebilir ancak sıralamaya uygun değildir. VaultPin alias'ın benzersiz, kullanılabilir veya uygun olacağını taahhüt etmez; Game Center hesabını ve profilini Apple kontrol eder.",
        ],
      },
      {
        heading: "6. Adil kullanım",
        paragraphs: [
          "Oyun, süre veya sıralamayı manipüle etmek için otomasyon, script, reverse engineering, değiştirilmiş istemci, sahte kimlik, API kötüye kullanımı, hizmet engelleme yöntemi veya benzer araçlar kullanma. Başka bir oyuncunun session'ına veya verisine erişmeye çalışma.",
          "VaultPin, hizmeti ve diğer oyuncuları korumak için makul ölçüde gerekli olduğunda geçersiz istekleri reddedebilir, uygun olmayan sonuçları kaldırabilir veya kötüye kullanılan erişimi kısıtlayabilir.",
        ],
      },
      {
        heading: "7. Tek seferlik satın alma",
        paragraphs: [
          "Orta, Zor ve Uzman pratik modları tek bir tüketilmeyen StoreKit satın alımı olarak birlikte sunulur. Abonelik yoktur. Apple'ın gösterdiği fiyat mağaza, para birimi ve vergi kurallarına göre değişebilir.",
          "Ödeme, geri yükleme ve iade taleplerini Apple kendi koşulları kapsamında işler. Satın alma ve geri yükleme Apple hesabına ve StoreKit'e bağlı olabilir. VaultPin oyun verilerini silmek Apple tarafından yönetilen satın alma hakkını kaldırmaz.",
        ],
      },
      {
        heading: "8. Apple koşulları",
        paragraphs: [
          "iOS uygulamasının kullanımı, farklı geçerli bir sözleşme yoksa Apple Standard Licensed Application End User License Agreement dahil Apple'ın geçerli kurallarına da tabidir. Resmî standart EULA https://www.apple.com/legal/internet-services/itunes/dev/stdeula/ adresindedir.",
        ],
      },
      {
        heading: "9. Fikri mülkiyet",
        paragraphs: [
          "VaultPin, özgün arayüzü, görselleri, sesleri, metinleri, oyun sunumu, yazılımı ve markası Miraç Doğan'a aittir veya geçerli haklar kapsamında kullanılır. Bu koşullar uygulamayı kullanman için kişisel, sınırlı, münhasır olmayan ve devredilemez bir hak verir; mülkiyet devretmez.",
        ],
      },
      {
        heading: "10. Kullanılabilirlik ve değişiklikler",
        paragraphs: [
          "Çevrimiçi hizmetler bakım, sağlayıcı kesintisi, ağ koşulları, güvenlik olayları veya Apple hizmetlerindeki değişiklikler nedeniyle kesilebilir. VaultPin makul ölçüde gerekli olduğunda özellikleri değiştirebilir, askıya alabilir veya sonlandırabilir. Kesintisiz kullanılabilirlik, belirli sıralama konumu veya Günlük Kasa sonucunun kalıcı tutulması taahhüt edilmez.",
        ],
      },
      {
        heading: "11. Garanti reddi",
        paragraphs: [
          "Geçerli hukukun izin verdiği ölçüde VaultPin, çevrimiçi bir oyun için makul olarak verilemeyecek ek garantiler olmadan “mevcut olduğu şekilde” sunulur. Bu hüküm, tüketiciler için hukuken hariç tutulamayan garanti veya başvuru yollarını sınırlamaz.",
        ],
      },
      {
        heading: "12. Sorumluluğun sınırlandırılması",
        paragraphs: [
          "Geçerli hukukun izin verdiği ölçüde VaultPin; hizmet kesintisi, sıralama konumunun kaybı veya makul kontrolü dışındaki uyumsuzluklardan kaynaklanan dolaylı ya da sonuçsal kayıplardan sorumlu değildir. Zorunlu tüketici korumaları dahil hukuken hariç tutulamayan sorumluluklar bu koşullarla kaldırılmaz.",
        ],
      },
      {
        heading: "13. Kullanımı bırakma ve veri silme",
        paragraphs: [
          "VaultPin'i kullanmayı istediğin zaman bırakabilir ve VaultPin oyuncu ile Günlük Kasa verileri için uygulama içi silme kontrolünü kullanabilirsin. Silme, sistem Game Center hesabından çıkış yapmaz veya Apple tarafından yönetilen satın alımları iptal etmez. Daha sonra Günlük Kasa'yı kullanırsan yeni bir oyuncu kaydı oluşturulabilir.",
        ],
      },
      {
        heading: "14. Uygulanacak hukuk",
        paragraphs: [
          "Bu koşullar, yaşadığın yerde geçerli hukukun sana sağladığı zorunlu koruma veya uyuşmazlık haklarını kaldırmadan Türkiye hukukuna tabidir. Yetkili mahkeme ve merciler geçerli hukuka göre belirlenir.",
        ],
      },
      {
        heading: "15. İletişim ve değişiklikler",
        paragraphs: [
          "Sorularını support@vaultpin.app adresine gönderebilirsin. Hizmet veya hukuki gereklilikler değiştiğinde bu koşullar güncellenebilir. Son güncelleme tarihi geçerli sürümü gösterir; değişiklikten sonra kullanım geçerli bildirim ve onay gerekliliklerine tabidir.",
        ],
      },
    ],
  },
};
